import React, { useState, useEffect, lazy, Suspense } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Link,
  useLocation,
} from "react-router-dom";
import Home from "./Home";
import "./App.css"; // Deine Index.css mit @tailwind imports
import logo from "./assets/logo.png";

// Unterseiten werden erst geladen, wenn sie aufgerufen werden (Code-Splitting).
// Vor allem Leaflet (Karte auf /standorte) bleibt so aus dem Start-Bundle raus.
const Locations = lazy(() => import("./Locations"));
const Kontakt = lazy(() => import("./Kontakt"));
const Datenschutz = lazy(() => import("./Datenschutz"));
const Impressum = lazy(() => import("./Impressum"));

// --- Scroll-to-Top Helper (damit man beim Seitenwechsel oben landet) ---
const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);

  const toggleMenu = () => setIsOpen(!isOpen);

  return (
    <nav className="fixed top-0 w-full z-[100] bg-white/80 backdrop-blur-md border-b border-gray-100 transition-all">
      <div className="max-w-[1400px] mx-auto px-6 py-4 flex justify-between items-center">
        {/* Logo Bereich */}
        <div className="flex items-center gap-3">
          <Link to="/">
            <img
              src={logo}
              alt="ShowOff Logo"
              className="h-10 w-auto object-contain cursor-pointer"
            />
          </Link>
        </div>

        {/* Desktop Links */}
        <div className="hidden md:flex gap-8 text-sm font-medium text-slate-600">
          <a href="/" className="hover:text-blue-700 transition-colors">
            Home
          </a>
          <a
            href="/standorte"
            className="hover:text-blue-700 transition-colors"
          >
            Standorte
          </a>
          <a href="/kontakt" className="hover:text-blue-700 transition-colors">
            Kontakt
          </a>
        </div>

        {/* Desktop CTA */}
        <a
          href="/kontakt"
          className="hidden md:block bg-slate-900 text-white px-6 py-2.5 rounded-full text-sm font-semibold hover:bg-blue-700 transition-all hover:shadow-lg hover:shadow-blue-700/20"
        >
          Anfrage senden
        </a>

        {/* Mobile Menu Button */}
        <button
          onClick={toggleMenu}
          className="md:hidden flex flex-col justify-center items-center w-10 h-10 gap-1.5 focus:outline-none"
        >
          <span
            className={`h-0.5 w-6 bg-slate-900 rounded-full transition-transform duration-300 ${
              isOpen ? "rotate-45 translate-y-2" : ""
            }`}
          ></span>
          <span
            className={`h-0.5 w-6 bg-slate-900 rounded-full transition-opacity duration-300 ${
              isOpen ? "opacity-0" : ""
            }`}
          ></span>
          <span
            className={`h-0.5 w-6 bg-slate-900 rounded-full transition-transform duration-300 ${
              isOpen ? "-rotate-45 -translate-y-2" : ""
            }`}
          ></span>
        </button>
      </div>

      {/* Mobile Menu Dropdown */}
      <div
        className={`md:hidden absolute top-full left-0 w-full bg-white border-b border-gray-100 shadow-xl transition-all duration-300 ease-in-out overflow-hidden ${
          isOpen ? "max-h-[400px] opacity-100" : "max-h-0 opacity-0"
        }`}
      >
        <div className="flex flex-col gap-4 p-8 bg-white text-lg font-medium text-slate-900">
          <a
            href="/"
            onClick={() => setIsOpen(false)}
            className="hover:text-blue-600 transition-colors"
          >
            Home
          </a>
          <a
            href="/standorte"
            onClick={() => setIsOpen(false)}
            className="hover:text-blue-600 transition-colors"
          >
            Standorte
          </a>
          <a
            href="/kontakt"
            onClick={() => setIsOpen(false)}
            className="hover:text-blue-600 transition-colors"
          >
            Kontakt
          </a>
          <hr className="border-gray-100 my-2" />
          <a
            href="/kontakt"
            onClick={() => setIsOpen(false)}
            className="bg-blue-800 text-white text-center py-4 rounded-full font-bold shadow-lg shadow-blue-600/20"
          >
            Anfrage senden
          </a>
        </div>
      </div>
    </nav>
  );
};

// --- Footer  ---
const Footer = () => {
  return (
    <footer className="bg-white text-black py-16 border-t border-dashed border-gray-400">
      <div className="w-full max-w-7xl mx-auto px-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-12">
          {/* Brand*/}
          <div className="md:col-span-2">
            <span className="self-center text-3xl font-bold whitespace-nowrap tracking-tighter mb-4 block">
              ShowOff.
            </span>
            <p className="text-gray-800 max-w-sm font-light leading-relaxed">
              Premium Außenwerbung in Nordrhein-Westfalen. Wir bringen Ihre
              Marke dorthin, wo das echte Leben passiert. Sichtbar. Relevant.
              Unübersehbar.
            </p>
          </div>

          {/* Links */}
          <div>
            <h2 className="mb-6 text-sm font-bold text-gray-700 uppercase tracking-widest">
              Unternehmen
            </h2>
            <ul className=" font-medium space-y-4">
              <li>
                <a href="/" className="transition-colors">
                  Home
                </a>
              </li>
              <li>
                <a href="/standorte" className=" transition-colors">
                  Standorte
                </a>
              </li>
              <li>
                <a href="/kontakt" className=" transition-colors">
                  Kontakt
                </a>
              </li>
            </ul>
          </div>

          {/* Links  */}
          <div>
            <h2 className="mb-6 text-sm font-bold text-gray-700 uppercase tracking-widest">
              Rechtliches
            </h2>
            <ul className=" font-medium space-y-4">
              <li>
                <a href="/datenschutz" className=" transition-colors">
                  Datenschutz
                </a>
              </li>
              <li>
                <a href="/impressum" className=" transition-colors">
                  Impressum
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div className="pt-8 border-t border-gray-800 flex flex-col md:flex-row justify-between items-center">
          <span className="text-sm text-gray-500 text-center md:text-left">
            © {new Date().getFullYear()} ShowOff
          </span>
        </div>
      </div>
    </footer>
  );
};

function App() {
  return (
    <Router>
      <ScrollToTop />
      <div className="flex flex-col min-h-screen bg-white text-gray-900 font-sans selection:bg-black selection:text-white">
        <Navbar />

        <main className="flex-grow">
          <Suspense fallback={<div className="min-h-[60vh]" />}>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/standorte" element={<Locations />} />
              <Route path="/kontakt" element={<Kontakt />} />
              <Route path="/datenschutz" element={<Datenschutz />} />{" "}
              <Route path="/impressum" element={<Impressum />} />
              {/* Hier weitere Routen */}
            </Routes>
          </Suspense>
        </main>

        <Footer />
      </div>
    </Router>
  );
}

export default App;
